import type {
  FlowMediaPlaylistsDto,
  MediaPlaylistDto,
  SessionMediaPlaylistsDto,
} from '@/types/dtos/media/media-playlist.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { MediaPlaylistAdvanceMode } from '@/types/dtos/media/media-playlist-advance-mode.enum';
import type { PlaylistNextShowMode } from '@/types/dtos/media/playlist-next-show-mode.enum';
import type { FlowPlaylistScope, NonSlotFlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';
import type {
  FlowEffectiveMediaSettingsDto,
  FlowMediaSettingsCapabilitiesDto,
  FlowMediaSettingsDto,
  SessionEffectiveMediaSettingsDto,
  SessionMediaSettingsCapabilitiesDto,
  SessionMediaSettingsDto,
} from '@/types/dtos/media/media-settings.dto';
import { getObjectId } from '@/helpers/object.id.helper';

export type FlowPlaylistSource = {
  id: string;
  origin?: string | { id: string };
  media?: MediaDto[];
  mediaPlaylists?: FlowMediaPlaylistsDto;
  liveContext?: { isRunning?: boolean };
  mediaSettings?: FlowMediaSettingsDto;
  effectiveMediaSettings?: FlowEffectiveMediaSettingsDto;
  mediaSettingsCapabilities?: FlowMediaSettingsCapabilitiesDto;
};

export function mediaDtosFromPlaylist(playlist?: MediaPlaylistDto): MediaDto[] {
  if (!playlist?.refs?.length) {
    return [];
  }
  return [...playlist.refs]
    .sort((a, b) => a.sequence - b.sequence)
    .map((ref) => ref.media)
    .filter((m): m is MediaDto => Boolean(m));
}

function resolvePlaylistMedia(playlist: MediaPlaylistDto | undefined, flow: FlowPlaylistSource): MediaDto[] {
  const rows = buildPlaylistMediaRows(playlist, flow.media ?? []);
  if (rows.length > 0) {
    return rows.map((row) => row.media);
  }
  return [];
}

function resolveFlowSlotPlaylist(
  slots: Record<string, MediaPlaylistDto> | undefined,
  slotId: string,
): MediaPlaylistDto | undefined {
  if (!slots) {
    return undefined;
  }
  if (slots[slotId]) {
    return slots[slotId];
  }
  const normalized = getObjectId(slotId) ?? slotId;
  if (slots[normalized]) {
    return slots[normalized];
  }
  return Object.entries(slots).find(([key]) => (getObjectId(key) ?? key) === normalized)?.[1];
}

export function getFlowPlaylistMedia(flow: FlowPlaylistSource, scope: FlowPlaylistScope, slotId?: string): MediaDto[] {
  if (flow.mediaPlaylists) {
    if (scope === 'slot' && slotId) {
      const fromPlaylist = resolvePlaylistMedia(resolveFlowSlotPlaylist(flow.mediaPlaylists.slots, slotId), flow);
      if (fromPlaylist.length > 0) {
        return fromPlaylist;
      }
    } else {
      const fromPlaylist = resolvePlaylistMedia(flow.mediaPlaylists[scope as NonSlotFlowPlaylistScope], flow);
      if (fromPlaylist.length > 0) {
        return fromPlaylist;
      }
    }
  }

  if (scope === 'slot' && slotId) {
    return flow.media?.filter((m) => m.placement?.id === slotId) ?? [];
  }
  if (scope === 'flow_intro') {
    return flow.media?.filter((m) => !m.placement) ?? [];
  }
  if (scope === 'flow_outro' || scope === 'flow_transition' || scope === 'flow_resume') {
    return [];
  }
  if (scope === 'pre_flow' || scope === 'after_flow') {
    return [];
  }
  return [];
}

export function getStartFlowPlaylistMedia(flow: FlowPlaylistSource): MediaDto[] {
  return [...getFlowPlaylistMedia(flow, 'pre_flow'), ...getFlowPlaylistMedia(flow, 'flow_intro')];
}

export function getEndFlowPlaylistMedia(flow: FlowPlaylistSource): MediaDto[] {
  return [...getFlowPlaylistMedia(flow, 'flow_outro'), ...getFlowPlaylistMedia(flow, 'after_flow')];
}

export function hasFlowMediaPlaylists(
  flow: FlowPlaylistSource,
): flow is FlowPlaylistSource & { mediaPlaylists: FlowMediaPlaylistsDto } {
  return Boolean(flow.mediaPlaylists);
}

/**
 * Whether Add is shown on a playlist widget. Event/flow managers may add even when the host
 * self-create toggle is off; pure host contributors follow effectiveAllowSelfCreate only.
 */
export function playlistAllowsContributorAddMedia(
  effectiveAllowSelfCreate: boolean,
  playlist?: MediaPlaylistDto,
  parentCanManageMedia?: boolean,
): boolean {
  if (playlist?.permissions?.includes('manage')) {
    return true;
  }
  if (parentCanManageMedia) {
    return true;
  }
  return effectiveAllowSelfCreate;
}

/**
 * Whether a user may drag-reorder playlist refs.
 * Managers bypass via playlist `manage` or parent media-settings edit capability;
 * contributors require the effective allowReorder policy flag and playlist `reorder`.
 * Per-item advance / auto-loop / next-show-mode edits require MediaPlaylist `manage`
 * separately (see `canEditPlaylistAdvanceMode` in MediaList).
 */
export function playlistAllowsContributorReorder(
  effectiveAllowReorder: boolean,
  playlist?: MediaPlaylistDto,
  parentCanManageMedia?: boolean,
): boolean {
  if (playlist?.permissions?.includes('manage')) {
    return true;
  }
  if (parentCanManageMedia) {
    return true;
  }
  if (!effectiveAllowReorder) {
    return false;
  }
  return playlist?.permissions?.includes('reorder') ?? false;
}

export function getFlowPlaylist(
  flow: FlowPlaylistSource,
  scope: FlowPlaylistScope,
  slotId?: string,
): MediaPlaylistDto | undefined {
  if (!flow.mediaPlaylists) {
    return undefined;
  }
  if (scope === 'slot' && slotId) {
    return resolveFlowSlotPlaylist(flow.mediaPlaylists.slots, slotId);
  }
  return flow.mediaPlaylists[scope as NonSlotFlowPlaylistScope];
}

/** Whether a flow-control inline playlist panel has media or enabled virtual rows to show. */
export function hasFlowControlPlaylistVisibleContent(
  flow: FlowPlaylistSource,
  scope: FlowPlaylistScope,
  slotId?: string,
): boolean {
  const playlist = getFlowPlaylist(flow, scope, slotId);
  if (!playlist?.id) {
    return false;
  }
  const media = getFlowPlaylistMedia(flow, scope, slotId);
  if (buildPlaylistMediaRows(playlist, media).length > 0) {
    return true;
  }
  const applicable = playlist.applicableVirtualMedia ?? [];
  if (applicable.length === 0) {
    return false;
  }
  const enabledSlugs = new Set(playlist.effectiveVirtualMedia?.enabledSlugs ?? []);
  return applicable.some((item) => enabledSlugs.has(item.slug));
}

export function hasFlowPlaylistScope(flow: FlowPlaylistSource, scope: NonSlotFlowPlaylistScope): boolean {
  return Boolean(getFlowPlaylist(flow, scope));
}

export type PlaylistMediaRow = {
  usageId: string;
  advanceMode?: MediaPlaylistAdvanceMode;
  nextShowMode?: PlaylistNextShowMode;
  loopEnabled?: boolean;
  dwellMs?: number;
  media: MediaDto;
};

export type SessionPlaylistSource = {
  id: string;
  origin?: string | { id: string };
  isInLibrary?: boolean;
  media?: MediaDto[];
  mediaPlaylists?: SessionMediaPlaylistsDto;
  mediaSettings?: SessionMediaSettingsDto;
  effectiveMediaSettings?: SessionEffectiveMediaSettingsDto;
  mediaSettingsCapabilities?: SessionMediaSettingsCapabilitiesDto;
};

export function getSessionPlaylist(session: SessionPlaylistSource): MediaPlaylistDto | undefined {
  return session.mediaPlaylists?.session;
}

/** Prefer the media copy with more / newer asset versions (stale playlist embeds lose to fresh session.media). */
export function mediaAssetsFreshnessScore(media?: MediaDto): number {
  const assets = media?.assets ?? [];
  if (assets.length === 0) return 0;
  const maxVersion = assets.reduce((max, asset) => Math.max(max, asset.version), 0);
  return assets.length * 1_000_000 + maxVersion;
}

export function pickFresherMedia(primary?: MediaDto, secondary?: MediaDto): MediaDto | undefined {
  if (!primary) return secondary;
  if (!secondary) return primary;
  if (primary.id !== secondary.id) return primary;
  return mediaAssetsFreshnessScore(secondary) > mediaAssetsFreshnessScore(primary) ? secondary : primary;
}

export function getSessionPlaylistMedia(session: SessionPlaylistSource): MediaDto[] {
  const playlist = getSessionPlaylist(session);
  const fromPlaylist = buildPlaylistMediaRows(playlist, session.media ?? []);
  if (fromPlaylist.length > 0) {
    return fromPlaylist.map((row) => row.media);
  }
  return session.media ?? [];
}

export function buildPlaylistMediaRows(
  playlist: MediaPlaylistDto | undefined,
  mediaFallback: MediaDto[] = [],
): PlaylistMediaRow[] {
  if (!playlist?.refs?.length) {
    return [];
  }
  const mediaById = new Map(mediaFallback.map((m) => [m.id, m]));
  return [...playlist.refs]
    .sort((a, b) => a.sequence - b.sequence)
    .map((ref) => {
      // Embedded ref.media is often stale after upload while session.media already has the new version.
      const media = pickFresherMedia(ref.media, mediaById.get(ref.mediaId));
      if (!media) {
        return null;
      }
      const row: PlaylistMediaRow = { usageId: ref.usageId, media };
      if (ref.advanceMode !== undefined) {
        row.advanceMode = ref.advanceMode;
      }
      if (ref.nextShowMode !== undefined) {
        row.nextShowMode = ref.nextShowMode;
      }
      if (ref.loopEnabled !== undefined) {
        row.loopEnabled = ref.loopEnabled;
      }
      if (ref.dwellMs !== undefined) {
        row.dwellMs = ref.dwellMs;
      }
      return row;
    })
    .filter((row): row is PlaylistMediaRow => row !== null);
}

export function findPlaylistRefForMediaItem(
  flow: FlowPlaylistSource,
  itemId: string,
  scope?: string,
  slotId?: string,
): PlaylistMediaRow | undefined {
  if (!itemId.startsWith('media:')) {
    return undefined;
  }
  const mediaId = itemId.slice('media:'.length);
  const playlists: MediaPlaylistDto[] = [];
  if (flow.mediaPlaylists) {
    if (scope === 'slot' && slotId) {
      const slotPlaylist = resolveFlowSlotPlaylist(flow.mediaPlaylists.slots, slotId);
      if (slotPlaylist) {
        playlists.push(slotPlaylist);
      }
    } else if (scope && scope !== 'slot') {
      const scoped = flow.mediaPlaylists[scope as NonSlotFlowPlaylistScope];
      if (scoped) {
        playlists.push(scoped);
      }
    } else {
      playlists.push(
        ...([
          flow.mediaPlaylists.pre_flow,
          flow.mediaPlaylists.flow_intro,
          flow.mediaPlaylists.flow_transition,
          flow.mediaPlaylists.flow_resume,
          flow.mediaPlaylists.flow_outro,
          flow.mediaPlaylists.after_flow,
        ].filter(Boolean) as MediaPlaylistDto[]),
        ...Object.values(flow.mediaPlaylists.slots ?? {}),
      );
    }
  }

  for (const playlist of playlists) {
    const rows = buildPlaylistMediaRows(playlist, flow.media ?? []);
    const match = rows.find((row) => row.media.id === mediaId);
    if (match) {
      return match;
    }
  }
  return undefined;
}
